"use client";

import { useEffect, useRef } from "react";
import { useClientValue } from "@/hooks/useClientValue";
import { usePathname, useSearchParams, useRouter } from "next/navigation";
import { useSession } from "@/contexts/session";
import {
	clearPendingActiveInvestingEntity,
	extractActiveInvestingEntity,
	nextHandledSwitch,
	readPendingActiveInvestingEntity,
	stripActiveInvestingEntity,
	writePendingActiveInvestingEntity,
} from "@/components/utilities/activeInvestingEntity/pendingActiveInvestingEntity";

const SET_ACTIVE_ROUTE = "/set-active-investing-entity";

/**
 * Pages that drive the sign-in flow themselves. They read the URL they were
 * given and navigate off it, and `/login` clears the MFA challenge whenever it
 * mounts — so rewriting the URL or redirecting from under them strands the
 * flow. Park the intent there and let them finish.
 */
const AUTH_FLOW_ROUTES = ["/login", "/auth-redirect"];

/**
 * Mounted once in the root layout, so it sees the `active_investing_entity`
 * deep-link parameter on whatever route the link happens to point at.
 *
 * With a session it navigates straight to the switch route. Without one the
 * switch is a mutation the visitor cannot make yet, so the id is parked in
 * session storage and replayed after they authenticate. Parking is what makes
 * the link robust: the id no longer has to survive in the URL, so it does not
 * matter that a server guard may redirect without the query string, or that
 * `useRedirectToLogin` prefers an existing `returnUrl` over the current
 * location.
 */
const RouteActiveInvestingEntity = () => {
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const { hasAuthenticatedSession } = useSession();
	const isClient = useClientValue(
		() => true,
		() => false,
	);
	const handledSwitchRef = useRef<string | null>(null);

	useEffect(() => {
		if (!isClient) {
			return;
		}

		// The switch route owns the mutation; acting here would navigate away
		// before it runs, and re-entering this effect would loop.
		if (pathname.startsWith(SET_ACTIVE_ROUTE)) {
			return;
		}

		const requestedId = extractActiveInvestingEntity(searchParams);
		const isAuthFlowRoute = AUTH_FLOW_ROUTES.some((route) =>
			pathname.startsWith(route),
		);

		if (!hasAuthenticatedSession || isAuthFlowRoute) {
			if (requestedId) {
				writePendingActiveInvestingEntity(requestedId);
			}
			return;
		}

		const investingEntityId = requestedId ?? readPendingActiveInvestingEntity();
		const returnUrl = stripActiveInvestingEntity(pathname, searchParams);
		const plan = nextHandledSwitch(
			handledSwitchRef.current,
			investingEntityId,
			returnUrl,
		);
		handledSwitchRef.current = plan.handledKey;

		if (!plan.shouldNavigate || !investingEntityId) {
			return;
		}

		clearPendingActiveInvestingEntity();
		// eslint-disable-next-line react-doctor/nextjs-no-client-side-redirect -- The pending investing entity lives in browser storage and is cleared before navigating.
		router.push(
			`${SET_ACTIVE_ROUTE}/${encodeURIComponent(investingEntityId)}?returnUrl=${encodeURIComponent(returnUrl)}`,
		);
	}, [isClient, hasAuthenticatedSession, pathname, searchParams, router]);

	return null;
};

export default RouteActiveInvestingEntity;
